import jsPDF from "jspdf";
// eslint-disable-next-line
import autoTable from "jspdf-autotable";
import { setDefault, minReqHeight, totalPagesExp } from "../../helpers/pdfHelper";
import { GroupType, OState, RollType } from "../../utils/types";
import { getGroupName } from "../grouping/groupingHelper";
import { toFixed } from "../../utils/helpers";
import { getRollNo } from "../../helpers/warehouseHelper";
import { GROUP_WISE, LAY_WISE, REMNANT_LAY } from "../../constants";

const print2TableColumnWise = (doc, startY, header, data, title) => {
  let YAxis;
  doc.text(title || "", 7, startY + 7);
  if (data.length > 3) {
    let mid;
    mid = data.length % 2 === 0 ? parseInt(data.length / 2) : parseInt(data.length / 2) + 1;

    doc.autoTable({
      startY: startY + 10,
      margin: { left: 7, right: 107 },
      columns: header,
      body: data.slice(0, mid)
    });
    YAxis = doc.autoTable.previous.finalY;

    // Reset page to the same as before previous table
    let newPage = doc.internal.getCurrentPageInfo().pageNumber;

    doc.setPage(1 + doc.internal.getCurrentPageInfo().pageNumber - doc.autoTable.previous.pageCount);
    doc.autoTable({
      startY: startY + 10,
      margin: { left: 107, right: 7 },
      columns: header,
      body: data.slice(mid, data.length)
    });
    if (doc.internal.getCurrentPageInfo().pageNumber > newPage) {
      newPage = doc.internal.getCurrentPageInfo().pageNumber;
    }
    doc.setPage(newPage);
  } else {
    doc.autoTable({
      startY: startY + 10,
      margin: { left: 7, right: 107 },
      columns: header,
      body: data.slice(0, data.length)
    });
    YAxis = doc.autoTable.previous.finalY;
  }
  return YAxis;
};

export const printFabricRelease = (order, orderFlow, fabric, ledger, rollList, dataType) => {
  if (order && ledger && orderFlow.groupList) {
    var doc = new jsPDF();
    const heading = "Fabric Release for " + order.customer.name + " #" + order.poRef;

    setDefault(doc, heading);
    doc.setFontSize(10);
    const finalGroups = orderFlow.groupList && orderFlow.groupList.filter(g => g.type === GroupType.FINAL);
    const shrinkageGroups = orderFlow.groupList && orderFlow.groupList.filter(g => g.type === GroupType.SHRINKAGE);
    let color = rollList && rollList[0] && rollList[0].color;
    let orderInfo = orderInfoData(order, orderFlow, color);
    let fabricReleaseRolls = fabricReleaseData(order, fabric, ledger, rollList, orderFlow, dataType);

    const pageHeight = doc.internal.pageSize.getHeight();

    ////// Print Order Info //////////////
    let startY = 10;
    doc.text("Order Info", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: orderInfo.header, body: orderInfo.data });
    ////// Print Fabric Summary //////////////
    startY = doc.autoTable.previous.finalY;
    doc.text("Fabric Summary", 7, startY + 7);
    doc.autoTable({ startY: startY + 10, columns: fabricReleaseRolls.summary.header, body: fabricReleaseRolls.summary.data });

    startY = doc.autoTable.previous.finalY;

    ////// Print group wise data //////////////
    if (dataType === GROUP_WISE) {
      startY = doc.autoTable.previous.finalY;
      startY = print2TableColumnWise(doc, startY, fabricReleaseRolls.normal.header, fabricReleaseRolls.normal.data, "Planned Rolls");
      ////// Print lay wise data //////////////
    } else if (dataType === LAY_WISE && orderFlow?.orderConfig?.layType && fabricReleaseRolls?.lays) {
      fabricReleaseRolls.lays.forEach(lay => {
        if (pageHeight - startY < minReqHeight) {
          doc.addPage();
          startY = 10;
        }
        if (lay.data?.length === 0) {
          doc.text(lay.heading, 7, startY + 10);
          doc.text("No fresh roll used in this lay", 7, startY + 17);
          startY = startY + 20;
        } else {
          startY = print2TableColumnWise(doc, startY, lay.header, lay.data, lay.heading);
        }
      });
    }

    if (fabricReleaseRolls.extra) {
      if (pageHeight - startY < minReqHeight) {
        doc.addPage();
        startY = 10;
      }
      startY = print2TableColumnWise(doc, startY, fabricReleaseRolls.extra.header, fabricReleaseRolls.extra.data, "Extra Rolls");
    }

    if (fabricReleaseRolls.rejected) {
      if (pageHeight - startY < minReqHeight) {
        doc.addPage();
        startY = 10;
      }
      startY = print2TableColumnWise(doc, startY, fabricReleaseRolls.rejected.header, fabricReleaseRolls.rejected.data, "Rejected Rolls");
    }

    // Print Shade Summary
    const headerShadeSummary = [
      { dataKey: "groupName", title: "Group" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "fabric", title: "Fabric" }
    ];

    const shadeSummary = rollList
      .filter(r => r.groupId && r.rollType === RollType.NORMAL)
      .map(r => `${r.groupId}-${r.shade}`)
      .reduce(
        (acc, groupShade) => ({
          ...acc,
          [groupShade]: rollList
            .filter(r => r.groupId === Number(groupShade.split("-")[0]) && r.shade === groupShade.split("-")[1])
            .reduce((acc, r) => acc + r.length, 0)
        }),
        {}
      );

    let dataShadeSummary = Object.keys(shadeSummary)
      .map(shadeGroup => {
        const groupId = Number(shadeGroup.split("-")[0]);
        const shade = shadeGroup.split("-")[1];
        let group = {};
        if (finalGroups.length > 0) {
          group = finalGroups.find(g => g.id === groupId);
        } else {
          group = shrinkageGroups.find(g => g.id === groupId);
        }
        return { group, groupName: getGroupName(group), shade, fabric: toFixed(shadeSummary[shadeGroup], 1) };
      })
      .sort((a, b) => (a.group?.name === b.group?.name ? (a.shade < b.shade ? -1 : 1) : a.group?.name < b.group?.name ? -1 : 1));
    if (pageHeight - startY < minReqHeight) {
      doc.addPage();
      startY = 10;
    }
    startY = print2TableColumnWise(doc, startY, headerShadeSummary, dataShadeSummary, "Shade Summary");

    // Print Split Rolls
    const rolls = rollList.filter(r => r.remarks && r.remarks.includes("Split by software"));
    // startY = startY + 15;
    if (rolls.length > 0) {
      doc.setFontSize(10);
      doc.text("Rolls split by Software", 7, startY + 7);
      doc.setFontSize(8);
      doc.text(
        rolls
          .sort((a, b) => (a.rollNumber < b.rollNumber ? -1 : 1))
          .map(r => r.rollNumber)
          .join(", "),
        7,
        startY + 12
      );
    }

    doc.putTotalPages(totalPagesExp);
    doc.save("FABRIC RELEASE " + order.customer.name + " " + order.poRef + ".pdf");
  }
};

const orderInfoData = (order, orderFlow, color) => {
  let orderInfo = {};
  if (order) {
    let { styleName, customer, customerPoRef, poRef, season, totalQty, extra } = order;
    const header = ["Customer", "Customer PO Ref", "PO Ref", "Part", "Lot", "Color", "Season", "Style", "Order Qty", "Extra(%)"];
    const data = [
      [customer.name, customerPoRef || "", poRef, orderFlow.part, "Lot-" + orderFlow.lot, color, season.name, styleName, totalQty, extra + "%"]
    ];
    orderInfo = { header, data };
  }
  return orderInfo;
};

const fabricReleaseData = (order, fabric, ledger, rollInfoList, orderFlow, dataType) => {
  let fabricReleaseData = {};
  if (order && orderFlow && fabric && ledger) {
    let rollList = rollInfoList
      .map(r => ({ ...r, group: orderFlow.groupList.find(g => g.id === r.groupId) || {} }))
      .sort((a, b) => (a.group && b.group && a.group.name === b.group.name ? (a.shade < b.shade ? -1 : 1) : a.group.name < b.group.name ? -1 : 1));

    let extraRolls = rollList.filter(r => r.rollType === RollType.EXTRA);
    let rejectedRolls = rollList.filter(r => r.rollType === RollType.REJECTED);
    let normalRolls = rollList.filter(r => r.rollType === RollType.NORMAL);

    let header = [
      { title: "Sl. No." },
      { title: "Roll Number" },
      { title: "Style Color" },
      { title: "Group" },
      { title: "Shade" },
      { title: "Length" },
      { title: "Rack No" },
      { title: "Remarks" }
    ];
    // Group wise data
    if (dataType === GROUP_WISE) {
      if (normalRolls.length > 0) {
        fabricReleaseData["normal"] = { header, data: getRollData(normalRolls, orderFlow) };
      }
      // Lay wise data
    } else if (dataType === LAY_WISE && orderFlow?.orderConfig?.laySlip) {
      let layMap = JSON.parse(orderFlow.orderConfig.laySlip);
      let layList = Object.keys(layMap)
        .filter(key => key !== REMNANT_LAY)
        .flatMap(lay => layMap[lay]);
      let mainRolls = [];
      let lays = layList
        .sort((a, b) => a.layNo - b.layNo)
        .map(lay => {
          const rollNos = lay.rowList?.filter(row => row.currLength === row.length).map(row => row.rollNo);
          mainRolls = [...mainRolls, ...rollNos];
          const filteredRolls = rollList.filter(r => rollNos.includes(r.rollNumber) || rollNos.includes(getRollNo(r.rollNumber)));
          const heading = "Lay " + lay.layNo;
          return { heading, header, data: getRollData(filteredRolls, orderFlow) };
        });

      const remnantRollList = rollList.filter(r => !mainRolls.includes(r.rollNumber));
      lays.push({ heading: REMNANT_LAY, header, data: getRollData(remnantRollList, orderFlow) });

      fabricReleaseData["lays"] = lays;
    }

    if (orderFlow.state === "Closed") {
      extraRolls = JSON.parse(orderFlow?.orderConfig?.extraRolls || "[]");
      rejectedRolls = JSON.parse(orderFlow?.orderConfig?.rejectedRolls || "[]");
      rollList = [...rollList, ...extraRolls, ...rejectedRolls];
    }

    if (extraRolls.length > 0) {
      fabricReleaseData["extra"] = { header, data: getRollData(extraRolls, orderFlow) };
    }
    if (rejectedRolls.length > 0) {
      fabricReleaseData["rejected"] = { header, data: getRollData(rejectedRolls, orderFlow) };
    }

    let headerSummary = [
      { title: "Item Code", dataKey: "itemCode" },
      { title: "Article", dataKey: "article" },
      { title: "Total Fabric", dataKey: "total" },
      { title: "Extra Fabric", dataKey: "extra" },
      { title: "Rejected Fabric", dataKey: "rejected" },
      { title: "Planned Fabric", dataKey: "normal" }
    ];

    let total = 0,
      rejected = 0,
      normal = 0,
      extra = 0;
    total = rollList.reduce((acc, el) => acc + el.length, 0);
    rejected = rejectedRolls.reduce((acc, el) => acc + el.length, 0);
    extra = extraRolls.reduce((acc, el) => acc + el.length, 0);
    normal = normalRolls.reduce((acc, el) => acc + el.length, 0);

    let dataSummary = [
      {
        itemCode: fabric.itemCode,
        article: fabric.articleName,
        total: toFixed(total, 1),
        extra: toFixed(extra, 1),
        rejected: toFixed(rejected, 1),
        normal: toFixed(normal, 1)
      }
    ];
    fabricReleaseData["summary"] = { header: headerSummary, data: dataSummary };
  }
  return fabricReleaseData;
};

const getRollData = (rollList, orderFlow) => {
  let rows = rollList.map((r, idx) => [
    idx + 1,
    r.rollNumber,
    r.color,
    orderFlow && orderFlow.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue ? r.group?.name : r.group?.name.replace("G", "P"),
    r.shade || "",
    toFixed(r.length, 1),
    r.rakeNo || "",
    ""
  ]);
  const totalLength = rollList.reduce((acc, roll) => acc + roll.length, 0);
  if (rollList.length > 1) {
    rows.push(["", "Total", "", "", "", toFixed(totalLength, 1), "", ""]);
  }
  return rows;
};
